import { Button } from "@/components/core/buttons/button";
import { SiteForm } from "@/components/sites/site-form";
import { useSiteUpdateMutation } from "@/features/dashboard/api/queries/site-query";
import type { Site } from "@/features/dashboard/types/site";
import * as Dialog from "@radix-ui/react-dialog";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { toast } from "sonner";

interface EditSiteModalProps {
	site: Site;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export function EditSiteModal({ site, isOpen, onClose, onSuccess }: EditSiteModalProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const updateMutation = useSiteUpdateMutation(site.id || "");

	const handleSubmit = async (values: Site) => {
		setIsSubmitting(true);
		try {
			await updateMutation.mutateAsync(values);
			toast.success("Site updated successfully");
			onSuccess?.();
			onClose();
		} catch (error) {
			console.log(error);
			toast.error("Failed to update site");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">Edit Site</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="py-4">
						<SiteForm site={site} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							form="site-form"
							disabled={isSubmitting}
							onClick={() => {
								const form = document.querySelector("#site-form") as HTMLFormElement;
								if (form) {
									form.requestSubmit();
								}
							}}
						>
							{isSubmitting ? "Updating..." : "Update Site"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
